import React, { useState, useEffect } from 'react';
import axios from '../utils/api';
import './AdminAttributeManager.css';

const AdminAttributeManager = () => {
  const [activeTab, setActiveTab] = useState('sizes'); // 'sizes' or 'colors'
  
  // Sizes state
  const [sizes, setSizes] = useState([]);
  const [sizeLoading, setSizeLoading] = useState(true);
  const [showSizeForm, setShowSizeForm] = useState(false);
  const [editingSize, setEditingSize] = useState(null);
  const [sizeName, setSizeName] = useState('');
  const [sizeValue, setSizeValue] = useState('');
  const [sizeUnit, setSizeUnit] = useState('');
  const [sizeDesc, setSizeDesc] = useState('');

  // Colors state
  const [colors, setColors] = useState([]);
  const [colorLoading, setColorLoading] = useState(true);
  const [showColorForm, setShowColorForm] = useState(false);
  const [editingColor, setEditingColor] = useState(null);
  const [colorName, setColorName] = useState('');
  const [hexCode, setHexCode] = useState('');
  const [colorDesc, setColorDesc] = useState('');

  // Alert state
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    onConfirm: null,
  });

  // Fetch data on mount
  useEffect(() => {
    fetchSizes();
    fetchColors();
  }, []);

  // Clear alerts after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  // Size handlers
  const fetchSizes = async () => {
    try {
      setSizeLoading(true);
      const response = await axios.get('/api/attributes/sizes');
      setSizes(response.data);
    } catch (err) {
      setError('Failed to load sizes: ' + (err.response?.data?.detail || err.message));
      console.error('Error fetching sizes:', err);
    } finally {
      setSizeLoading(false);
    }
  };

  const handleAddSize = async (e) => {
    e.preventDefault();
    if (!sizeName.trim()) {
      setError('Size name is required');
      return;
    }

    try {
      const payload = {
        name: sizeName,
        value: sizeValue ? parseFloat(sizeValue) : null,
        unit: sizeUnit || null,
        description: sizeDesc || null,
      };

      if (editingSize) {
        await axios.put(`/api/attributes/sizes/${editingSize.id}`, payload);
        setSuccess('Size updated successfully');
      } else {
        await axios.post('/api/attributes/sizes', payload);
        setSuccess('Size created successfully');
      }

      resetSizeForm();
      fetchSizes();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save size');
      console.error('Error saving size:', err);
    }
  };

  const resetSizeForm = () => {
    setSizeName('');
    setSizeValue('');
    setSizeUnit('');
    setSizeDesc('');
    setEditingSize(null);
    setShowSizeForm(false);
  };

  const handleEditSize = (size) => {
    setEditingSize(size);
    setSizeName(size.name);
    setSizeValue(size.value || '');
    setSizeUnit(size.unit || '');
    setSizeDesc(size.description || '');
    setShowSizeForm(true);
  };

  const openConfirmDialog = (title, message, confirmText, onConfirm) => {
    setConfirmDialog({
      show: true,
      title,
      message,
      confirmText,
      onConfirm,
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ show: false, title: '', message: '', confirmText: 'Confirm', onConfirm: null });
  };

  const executeConfirmDialogAction = async () => {
    const action = confirmDialog.onConfirm;
    closeConfirmDialog();
    if (typeof action === 'function') {
      await action();
    }
  };

  const handleDeleteSize = async (sizeId) => {
    openConfirmDialog('Delete Size', 'Are you sure you want to delete this size?', 'Delete', async () => {
      try {
        await axios.delete(`/api/attributes/sizes/${sizeId}`);
        setSuccess('Size deleted successfully');
        fetchSizes();
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to delete size');
        console.error('Error deleting size:', err);
      }
    });
  };

  // Color handlers
  const fetchColors = async () => {
    try {
      setColorLoading(true);
      const response = await axios.get('/api/attributes/colors');
      setColors(response.data);
    } catch (err) {
      setError('Failed to load colors: ' + (err.response?.data?.detail || err.message));
      console.error('Error fetching colors:', err);
    } finally {
      setColorLoading(false);
    }
  };

  const handleAddColor = async (e) => {
    e.preventDefault();
    if (!colorName.trim()) {
      setError('Color name is required');
      return;
    }

    try {
      const payload = {
        name: colorName,
        hex_code: hexCode || null,
        description: colorDesc || null,
      };

      if (editingColor) {
        await axios.put(`/api/attributes/colors/${editingColor.id}`, payload);
        setSuccess('Color updated successfully');
      } else {
        await axios.post('/api/attributes/colors', payload);
        setSuccess('Color created successfully');
      }

      resetColorForm();
      fetchColors();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save color');
      console.error('Error saving color:', err);
    }
  };

  const resetColorForm = () => {
    setColorName('');
    setHexCode('');
    setColorDesc('');
    setEditingColor(null);
    setShowColorForm(false);
  };

  const handleEditColor = (color) => {
    setEditingColor(color);
    setColorName(color.name);
    setHexCode(color.hex_code || '');
    setColorDesc(color.description || '');
    setShowColorForm(true);
  };

  const handleDeleteColor = async (colorId) => {
    openConfirmDialog('Delete Color', 'Are you sure you want to delete this color?', 'Delete', async () => {
      try {
        await axios.delete(`/api/attributes/colors/${colorId}`);
        setSuccess('Color deleted successfully');
        fetchColors();
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to delete color');
        console.error('Error deleting color:', err);
      }
    });
  };

  return (
    <div className="admin-attribute-container">
      <div className="header">
        <h1>Attribute Management</h1>
        <p>Define standard sizes and colors for your products</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="tabs">
        <button
          className={`tab-button ${activeTab === 'sizes' ? 'active' : ''}`}
          onClick={() => setActiveTab('sizes')}
        >
          📏 Sizes
        </button>
        <button
          className={`tab-button ${activeTab === 'colors' ? 'active' : ''}`}
          onClick={() => setActiveTab('colors')}
        >
          🎨 Colors
        </button>
      </div>

      {/* Sizes Tab */}
      {activeTab === 'sizes' && (
        <div className="tab-content">
          <div className="section-header">
            <h2>Size Standards</h2>
            <button
              className="btn btn-primary"
              onClick={() => {
                resetSizeForm();
                setShowSizeForm(true);
              }}
            >
              + Add Size
            </button>
          </div>

          {showSizeForm && (
            <form className="form-section" onSubmit={handleAddSize}>
              <h3>{editingSize ? 'Edit Size' : 'New Size'}</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Size Name *</label>
                  <input
                    type="text"
                    value={sizeName}
                    onChange={(e) => setSizeName(e.target.value)}
                    placeholder="e.g., Small, Medium, Large or XL"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Value</label>
                  <input
                    type="number"
                    step="0.1"
                    value={sizeValue}
                    onChange={(e) => setSizeValue(e.target.value)}
                    placeholder="e.g., 10.5"
                  />
                </div>
                <div className="form-group">
                  <label>Unit</label>
                  <select value={sizeUnit} onChange={(e) => setSizeUnit(e.target.value)}>
                    <option value="">Select unit...</option>
                    <option value="cm">cm</option>
                    <option value="inch">inch</option>
                    <option value="mm">mm</option>
                    <option value="kg">kg</option>
                    <option value="g">g</option>
                    <option value="ml">ml</option>
                    <option value="l">l</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={sizeDesc}
                  onChange={(e) => setSizeDesc(e.target.value)}
                  placeholder="Enter size description (optional)"
                  rows="2"
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-success">
                  {editingSize ? 'Update Size' : 'Create Size'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={resetSizeForm}>
                  Cancel
                </button>
              </div>
            </form>
          )}

          {sizeLoading ? (
            <div className="loading">Loading sizes...</div>
          ) : (
            <div className="items-grid">
              {sizes.length === 0 ? (
                <div className="empty-state">No sizes defined yet. Create one to get started!</div>
              ) : (
                sizes.map((size) => (
                  <div key={size.id} className="item-card size-card">
                    <div className="item-header">
                      <h3>{size.name}</h3>
                      {size.value && <span className="item-value">{size.value} {size.unit}</span>}
                    </div>
                    {size.description && <p className="item-description">{size.description}</p>}
                    <div className="item-actions">
                      <button
                        className="btn btn-small btn-edit"
                        onClick={() => handleEditSize(size)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-small btn-delete"
                        onClick={() => handleDeleteSize(size.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Colors Tab */}
      {activeTab === 'colors' && (
        <div className="tab-content">
          <div className="section-header">
            <h2>Color Standards</h2>
            <button
              className="btn btn-primary"
              onClick={() => {
                resetColorForm();
                setShowColorForm(true);
              }}
            >
              + Add Color
            </button>
          </div>

          {showColorForm && (
            <form className="form-section" onSubmit={handleAddColor}>
              <h3>{editingColor ? 'Edit Color' : 'New Color'}</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Color Name *</label>
                  <input
                    type="text"
                    value={colorName}
                    onChange={(e) => setColorName(e.target.value)}
                    placeholder="e.g., Red, Blue, Forest Green"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Hex Code</label>
                  <div className="hex-input-group">
                    <input
                      type="text"
                      value={hexCode}
                      onChange={(e) => setHexCode(e.target.value)}
                      placeholder="#FF5733"
                      pattern="^#[0-9A-Fa-f]{6}$"
                    />
                    <input
                      type="color"
                      value={hexCode || '#000000'}
                      onChange={(e) => setHexCode(e.target.value)}
                      className="color-picker"
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={colorDesc}
                  onChange={(e) => setColorDesc(e.target.value)}
                  placeholder="Enter color description (optional)"
                  rows="2"
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-success">
                  {editingColor ? 'Update Color' : 'Create Color'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={resetColorForm}>
                  Cancel
                </button>
              </div>
            </form>
          )}

          {colorLoading ? (
            <div className="loading">Loading colors...</div>
          ) : (
            <div className="items-grid">
              {colors.length === 0 ? (
                <div className="empty-state">No colors defined yet. Create one to get started!</div>
              ) : (
                colors.map((color) => (
                  <div key={color.id} className="item-card color-card">
                    {color.hex_code && (
                      <div className="color-preview" style={{ backgroundColor: color.hex_code }} />
                    )}
                    <div className="item-header">
                      <h3>{color.name}</h3>
                      {color.hex_code && <span className="hex-code">{color.hex_code}</span>}
                    </div>
                    {color.description && <p className="item-description">{color.description}</p>}
                    <div className="item-actions">
                      <button
                        className="btn btn-small btn-edit"
                        onClick={() => handleEditColor(color)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-small btn-delete"
                        onClick={() => handleDeleteColor(color.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {confirmDialog.show && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '520px' }}>
            <div className="modal-header">
              <h2>{confirmDialog.title || 'Confirm Action'}</h2>
              <button className="close-btn" onClick={closeConfirmDialog}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 0 }}>{confirmDialog.message}</p>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={closeConfirmDialog}>Cancel</button>
              <button type="button" className="btn btn-danger" onClick={executeConfirmDialogAction}>
                {confirmDialog.confirmText || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAttributeManager;
